import * as React from 'react'

import isEqual from 'lodash/isEqual'

import { useEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'

import compensationsCollection from 'modules/contracts/store/collections/compensations'
import taxonomies from 'modules/contracts/store/collections/taxonomies'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import draftSupplementsCollection from 'modules/payroll_bundle/store/collections/draftSupplements'
import observationsCollection from 'modules/payroll_bundle/store/collections/observations'
import supplementObservations from 'modules/payroll_bundle/store/collections/supplementObservations'
import supplementsCollection from 'modules/payroll_bundle/store/collections/supplements'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

// Based on useFetchData.
// The code in this file assumes that `dev_contract_versions` feature flag is enabled.
interface Params {
  policyPeriodId: number | null | undefined
  supplementsStore: SupplementsFormStore
  employeeIds: number[]
}

const useFetchSupplementsBatchByPeriod = ({
  policyPeriodId,
  supplementsStore,
  employeeIds,
}: Params) => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const [isNotFound, setIsNotFound] = React.useState<boolean>(false)

  const canSeeObservations = policyAuthorizations.for('payroll_supplements.observations.read')
  const canSeeSupplements = policyAuthorizations.for('payroll_supplements.supplements.read')

  // useRef to store the previous parameters
  const prevParamsRef = React.useRef<{
    policyPeriodId: number | null | undefined
    employeeIds: number[]
  }>({
    policyPeriodId: null,
    employeeIds: [],
  })

  const fetchSupplements = async (newEmployeeIds: number[] = []) => {
    setLoading(true)
    setIsNotFound(false)

    const newEmployeeIdsToLoad = newEmployeeIds.length ? newEmployeeIds : employeeIds

    const supplementDataPlaceholder = canSeeSupplements
      ? supplementsCollection.fetch({
          data: { policy_period_ids: [policyPeriodId], employee_ids: newEmployeeIdsToLoad },
        })
      : Promise.resolve([])

    const draftSupplementDataPlaceholder = canSeeSupplements
      ? draftSupplementsCollection.fetch({
          data: { payroll_policy_period_id: policyPeriodId, employee_ids: newEmployeeIdsToLoad },
        })
      : Promise.resolve([])

    // Employee observations (vs supplementObservations)
    const observationsDataPlaceholder = canSeeObservations
      ? observationsCollection.fetch({
          data: { payroll_policy_period_id: policyPeriodId, employee_ids: newEmployeeIdsToLoad },
        })
      : Promise.resolve([])

    const action = async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const [supplementsData, draftSupplementsData, observationsData]: any = await Promise.all([
        supplementDataPlaceholder,
        draftSupplementDataPlaceholder,
        observationsDataPlaceholder,
        taxonomies.fetch(),
      ])

      const allSupplements = [
        ...supplementsData,
        ...(Array.isArray(draftSupplementsData)
          ? draftSupplementsData.map((item) => {
              item.id = null
              return item
            })
          : []),
      ]

      supplementsStore.buildForms(allSupplements)
      supplementsStore.buildObservationForms(observationsData)

      const supplementObservationsQuery = supplementObservations.fetch()
      const supplementObservationsData = await supplementObservationsQuery
      supplementsStore.buildSupplementObservationForms(supplementObservationsData)

      const ids = allSupplements
        .filter((s) => s.contracts_compensation_id)
        .map((s) => s.contracts_compensation_id)

      await compensationsCollection.batchFetchByIds(ids, 100)
    }

    const { success } = await asyncAction(action, {
      successFlash: false,
      errorFlash: true,
    })
    setLoading(false)

    if (!success) {
      setIsNotFound(true)
    }
  }

  useEffect(() => {
    const { policyPeriodId: prevPolicyPeriodId, employeeIds: prevEmployeeIds } =
      prevParamsRef.current

    const policyPeriodChanged = policyPeriodId !== prevPolicyPeriodId
    const employeeIdsChanged = !isEqual(employeeIds, prevEmployeeIds)

    if (policyPeriodChanged || employeeIdsChanged) {
      fetchSupplements(employeeIds)
      prevParamsRef.current = { policyPeriodId, employeeIds: [...employeeIds] }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyPeriodId, employeeIds.join('-')])

  return {
    loading,
    isNotFound,
    fetchSupplements,
  }
}

export default useFetchSupplementsBatchByPeriod
